import React from 'react';
import { Box, Grid, Typography, List, ListItem, Checkbox, FormControlLabel, Button } from "@mui/material";
import swal from "sweetalert";
import { PuffLoader } from "react-spinners";
import { css } from "@emotion/react";
import { getAnswersByTopic, createWellnessAttributeAnswer, createTopicSummaryFromForm } from '../../utilityFunctions/WellnessUtil';
import Colors from "../../components/ui/Colors";
import { loaderAnimation } from '../../Api';
import { useHistory } from "react-router-dom";

const cssLoader = css`
  display: block;
  margin-left: auto;
  margin-right: auto;
`;

/**
 * ConsumerPlanDetail component
 * Displays detailed wellness attributes and choices for a specific topic
 * Allows users to select choices and submit their answers
 * 
 * @param {Object} props - Component props
 * @param {Object} props.loginUser - User login information with JWT token
 * @param {boolean} props.isEmbedded - Whether the component is embedded in another component
 * @returns {JSX.Element} - Rendered component
 */
export default function ConsumerPlanDetail(props) {
    const { loginUser, isEmbedded } = props;
    const history = useHistory();
    const wellnessTopic = props.location.state?.topic;

    const [loading, setLoading] = React.useState(false);
    const [checkedChoices, setCheckedChoices] = React.useState({});

    React.useEffect(() => {
        const loadInitialValues = async () => {
            if (!wellnessTopic) return;

            setLoading(true);
            const response = await getAnswersByTopic({ topicId: wellnessTopic.id, username: props.location?.state?.username ? props.location.state.username : null }, loginUser.jwtToken);
            setLoading(false);

            if (!response) {
                swal({ title: "Error", text: "Failed to get wellness topics", icon: "error", button: "OK" });
                return;
            }

            const initialCheckedChoices = response.reduce((acc, answer) => {
                acc[`${answer.wellnessAttributeId}-${answer.wellnessChoiceId}`] = true;
                return acc;
            }, {});

            console.log(initialCheckedChoices);
            setCheckedChoices(initialCheckedChoices);
        };

        loadInitialValues();
    }, [wellnessTopic, loginUser.jwtToken]);

    /**
     * Toggles a choice selection state
     * 
     * @param {number} attributeId - ID of the wellness attribute
     * @param {number} choiceId - ID of the wellness choice
     */
    const toggleChoice = (attributeId, choiceId) => {
        const choiceKey = `${attributeId}-${choiceId}`;
        setCheckedChoices(prevState => ({
            ...prevState,
            [choiceKey]: !prevState[choiceKey],
        }));
    };

    /**
     * Submits selected answers to the server
     * Creates a topic summary from the form data
     * Shows success message and navigates back to topics page if not embedded
     */
    const submitAnswers = async () => {
        setLoading(true);
        const selectedChoiceIds = Object.keys(checkedChoices)
            .filter(choiceKey => checkedChoices[choiceKey])
            .map(choiceKey => choiceKey.split('-')[1]);

        console.log(selectedChoiceIds);

        const wellnessAnswersRequest = {
            choicesIds: selectedChoiceIds,
            topicId: wellnessTopic.id,
            username: props.location?.state?.username ? props.location.state.username : null
        };

        const response = await createTopicSummaryFromForm(wellnessAnswersRequest, loginUser.jwtToken);
        setLoading(false);

        if (!response) {
            swal({ title: "Error", text: "Failed to submit the form", icon: "error", button: "OK" });
            return;
        }
        console.log(props.location?.state?.username);

        swal({ title: "Success", text: "Form submitted successfully", icon: "success", button: "OK" })
            .then(() => {
                if (!isEmbedded) {
                    history.push({
                        pathname: "/retirement_plan/topics",
                        state: {
                            username: props.location?.state?.username ? props.location.state.username : null
                        }
                    });
                }
            });
    };

    return (
        <Box sx={{ 
            display: "flex", 
            justifyContent: "center", 
            width: "100%", 
            height: "100%",
            padding: isEmbedded ? "1rem" : 0
        }}>
            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", width: {xs: "90%", sm: "70%", md: "50%"}, height: "90%", textAlign: "center", marginTop: { xs: "1.3rem", sm: 0 }}}>
                {loading ? loaderAnimation :
                    <Grid container direction="row" spacing={1} sx={{height: { xs: '80vh', sm: '80vh', md: '80vh'}, overflowY: 'auto', marginTop: {sx: "1rem", sm: "1rem", md: "3rem"}}}>
                        <Grid item xs={12}>
                            <List sx={{ width: "100%", marginTop: { xs: "3rem", sm: "2rem", md: "2rem" } }}>
                                {wellnessTopic.wellnessAttributes.map(attribute => (
                                    <ListItem key={attribute.id} disablePadding sx={{ marginBottom: "2.5rem" }}>
                                        <Grid container spacing={1}>
                                            <Grid item xs={12} sx={{ display: "flex", justifyContent: "start", textAlign: "start" }}>
                                                <Typography variant="body1" sx={{ fontWeight: "bold", color: "black" }}>{attribute.name}</Typography>
                                            </Grid>
                                            {attribute.wellnessChoices?.map(choice => (
                                                console.log(`${attribute.id}-${choice.id}`),
                                                <React.Fragment key={`attribute-${attribute.id}-choice-${choice.id}`}>
                                                    <Grid item xs={2} sx={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
                                                        <FormControlLabel
                                                            control={
                                                                <Checkbox
                                                                    color="secondary"
                                                                    checked={!!checkedChoices[`${attribute.id}-${choice.id}`]}
                                                                    onChange={() => toggleChoice(attribute.id, choice.id)}
                                                                />
                                                            }
                                                        />
                                                    </Grid>
                                                    <Grid item xs={10} sx={{ display: "flex", justifyContent: "start", alignItems: "center" }}>
                                                        <Typography variant="body2" sx={{ color: "dimGray" }}>{choice.question}</Typography>
                                                    </Grid>
                                                </React.Fragment>
                                            ))}
                                        </Grid>
                                    </ListItem>
                                ))}
                            </List>
                        </Grid>
                        <Grid item xs={12}>
                            <Button onClick={submitAnswers} variant="contained" sx={{ backgroundColor: Colors.primary, marginTop: "2rem" }}>
                                Submit
                            </Button>
                        </Grid>
                    </Grid>
                }
            </Box>
        </Box>
    );
}